import toast from 'react-hot-toast';

// Toast IDs for deduplication
const TOAST_IDS = {
  DEGRADED: 'api-degraded',
  OFFLINE: 'api-offline',
  RECOVERY: 'api-recovery',
  RATE_LIMITED: 'api-rate-limited',
  QUOTA_WARNING: 'api-quota-warning',
  THROTTLING: 'evaluation-throttling',
  THROTTLING_FAILED: 'evaluation-throttling-failed',
} as const;

type ToastType = 'degraded' | 'offline' | 'recovery' | 'rateLimited' | 'quotaWarning';

export const showDegradedToast = (serviceName: string = 'API'): void => {
  toast.loading(
    `Experiencing connectivity issues with ${serviceName}. Retrying...`,
    {
      id: TOAST_IDS.DEGRADED,
      duration: 5000,
      style: { background: '#FEF3C7', color: '#92400E', border: '1px solid #F59E0B' },
    }
  );
};

export const showOfflineToast = (serviceName: string = 'API'): void => {
  toast.dismiss(TOAST_IDS.DEGRADED);
  toast.error(
    `${serviceName} is currently unavailable. Some features may not work.`,
    {
      id: TOAST_IDS.OFFLINE,
      duration: Infinity,
      style: { background: '#FEE2E2', color: '#991B1B', border: '1px solid #EF4444' },
    }
  );
};

export const showRecoveryToast = (serviceName: string = 'API'): void => {
  toast.dismiss(TOAST_IDS.DEGRADED);
  toast.dismiss(TOAST_IDS.OFFLINE);
  toast.success(
    `Connection to ${serviceName} restored`,
    {
      id: TOAST_IDS.RECOVERY,
      duration: 3000,
      style: { background: '#D1FAE5', color: '#065F46', border: '1px solid #10B981' },
    }
  );
};

export const showRateLimitedToast = (retryAfterSeconds: number = 60): void => {
  toast.error(
    `Rate limit reached. Retry in ${retryAfterSeconds}s`,
    {
      id: TOAST_IDS.RATE_LIMITED,
      duration: Math.min(retryAfterSeconds * 1000, 10000),
      style: { background: '#FEE2E2', color: '#991B1B', border: '1px solid #EF4444' },
    }
  );
};

export const showQuotaWarningToast = (percentUsed: number, remaining: number, resetSeconds: number): void => {
  const message =
    percentUsed >= 100
      ? `API limit reached! Resets in ${resetSeconds}s`
      : percentUsed >= 80
      ? `API limit almost reached (${remaining} left). Resets in ${resetSeconds}s`
      : `Approaching API limit (${remaining} left). Resets in ${resetSeconds}s`;

  const bgColor = percentUsed >= 100 ? '#FEE2E2' : '#FEF3C7';
  const textColor = percentUsed >= 100 ? '#991B1B' : '#92400E';
  const borderColor = percentUsed >= 100 ? '#EF4444' : '#F59E0B';

  toast(message, {
    id: TOAST_IDS.QUOTA_WARNING,
    duration: 5000,
    style: { background: bgColor, color: textColor, border: `1px solid ${borderColor}` },
  });
};

export const showThrottlingToast = (retryAt: string, retryCount: number, maxRetries: number = 3): void => {
  const secondsUntilRetry = Math.max(0, Math.ceil((new Date(retryAt).getTime() - new Date().getTime()) / 1000));
  toast.loading(
    `AWS rate limit reached. Automatically retrying in ${secondsUntilRetry}s (attempt ${retryCount}/${maxRetries})`,
    {
      id: TOAST_IDS.THROTTLING,
      duration: secondsUntilRetry * 1000 + 5000,
      style: { background: '#FEF3C7', color: '#92400E', border: '1px solid #F59E0B' },
    }
  );
};

export const showThrottlingFailedToast = (): void => {
  toast.dismiss(TOAST_IDS.THROTTLING);
  toast.error(
    'Evaluation failed due to AWS rate limits after 3 attempts. Try again later or contact support to increase quotas.',
    {
      id: TOAST_IDS.THROTTLING_FAILED,
      duration: 10000,
      style: { background: '#FEE2E2', color: '#991B1B', border: '1px solid #EF4444' },
    }
  );
};

export const dismissThrottlingToast = (): void => {
  toast.dismiss(TOAST_IDS.THROTTLING);
};

export const dismissAllApiToasts = (): void => {
  Object.values(TOAST_IDS).forEach((id) => toast.dismiss(id));
};

export const dismissApiToast = (toastType: ToastType): void => {
  const toastId: Record<ToastType, string> = {
    degraded: TOAST_IDS.DEGRADED,
    offline: TOAST_IDS.OFFLINE,
    recovery: TOAST_IDS.RECOVERY,
    rateLimited: TOAST_IDS.RATE_LIMITED,
    quotaWarning: TOAST_IDS.QUOTA_WARNING,
  };
  const id = toastId[toastType];
  if (id) toast.dismiss(id);
};

const apiToasts = {
  showDegradedToast,
  showOfflineToast,
  showRecoveryToast,
  showRateLimitedToast,
  showQuotaWarningToast,
  showThrottlingToast,
  showThrottlingFailedToast,
  dismissThrottlingToast,
  dismissAllApiToasts,
  dismissApiToast,
  TOAST_IDS,
};
export default apiToasts;
